import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { view_ApiManager } from "../ApiManager/ApiManager";
import momentTz from "moment-timezone";
import Paginations from "../GenericComponents/Pagination";
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaStickyNote,
  FaUserTimes,
} from "react-icons/fa";
import { days as dayPresets } from "../../utilis/constants";
import "../../styles/tailwind.css";
import { AttendanceStatusBadge } from "../HRManagement/attendanceStatusBadge";
import {
  ATT_TABLE_CELL,
  ATT_TABLE_HEAD,
  ATT_TABLE_HEAD_CENTER,
  ATT_TABLE_ROW,
  AttendanceEmpty,
  AttendanceFilterField,
  AttendanceFilterQuickGroup,
  AttendanceFilterRow,
  AttendanceFiltersPanel,
  AttendanceHoursBadge,
  AttendanceKpiCard,
  AttendanceKpiGrid,
  AttendancePageShell,
  AttendanceSelect,
  AttendanceTableCard,
  AttendanceTableWrap,
  AttendanceTimePill,
  AttendanceDateInput,
  AttendanceDateChip,
  employeeInitials,
} from "../HRManagement/attendanceUiShared";

const AttendenceForEmployee = () => {
  const { userid } = useParams();
  const [record, setRecord] = useState([]);
  const [loading, setLoading] = useState(true);
  const [perItem, setPerItem] = useState(0);
  const [totalItem, setTotalItem] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [sDate, setSDate] = useState("");
  const [eDate, setEDate] = useState("");
  const [days, setDays] = useState(30);

  const fetchRecords = useCallback(() => {
    setLoading(true);
    const start = sDate || null;
    const end = eDate || null;
    const dayFilter = days || null;
    const url = `employeeAttendanceRecord/${userid}/${start}/${end}/${dayFilter}`;
    view_ApiManager(
      url,
      currentPage,
      perPage,
      (res) => {
        setRecord(res.data.data["data"] || []);
        setTotalItem(res.data.data["total"] || 0);
        setPerPage(res.data.data["per_page"] || perPage);
        setLastPage(res.data.data["last_page"] || 1);
        setPerItem((res.data.data["data"] || []).length);
        setLoading(false);
      },
      () => setLoading(false),
    );
  }, [userid, sDate, eDate, days, currentPage, perPage]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handlePaginationCallback = (page, newPerPage) => {
    if (newPerPage && newPerPage !== "") {
      setCurrentPage(1);
      setPerPage(Number(newPerPage));
    } else {
      setCurrentPage(page);
    }
  };

  const applyDayPreset = (value) => {
    setDays(value);
    setSDate("");
    setEDate("");
    setCurrentPage(1);
  };

  const onStartDateChange = (value) => {
    setSDate(value);
    setDays(null);
    setCurrentPage(1);
    if (!value) setEDate("");
  };

  const onEndDateChange = (value) => {
    setEDate(value);
    setDays(null);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setDays(30);
    setSDate("");
    setEDate("");
    setCurrentPage(1);
  };

  const formatClock = (iso, tz) => {
    if (!iso) return null;
    return momentTz.utc(iso).tz(tz || "UTC").format("h:mm A");
  };

  const formatDateLine = (iso, tz) => {
    if (!iso) return null;
    return momentTz.utc(iso).tz(tz || "UTC").format("ddd, DD MMM YYYY");
  };

  const employeeName = record?.[0]?.user || "Employee";
  const branchName = record?.[0]?.branch;

  const kpi = useMemo(() => {
    const norm = (s) => (s || "").toLowerCase();
    let onTime = 0;
    let late = 0;
    let absent = 0;
    record.forEach((row) => {
      const st = norm(row.status);
      if (st.includes("on time") || st === "present") onTime += 1;
      else if (st.includes("late")) late += 1;
      else if (st === "absent") absent += 1;
    });
    return {
      rows: record.length,
      onTime,
      late,
      absent,
    };
  }, [record]);

  const showEndDate = Boolean(sDate);

  return (
    <AttendancePageShell>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[#E5E7EB] bg-white px-3 py-2.5 shadow-sm">
        <div className="flex min-w-0 items-center gap-2.5">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-500 no-underline hover:text-[#007BFF]"
          >
            <FaArrowLeft size={11} />
            Dashboard
          </Link>
          <span
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#007BFF] text-xs font-bold text-white shadow-sm"
            aria-hidden
          >
            {employeeInitials(employeeName)}
          </span>
          <div className="min-w-0">
            <h1 className="m-0 truncate text-base font-bold text-[#35314d] sm:text-lg">
              {employeeName}
            </h1>
            <p className="m-0 truncate text-[12px] text-gray-500">
              {branchName ? `${branchName} · ` : ""}
              Attendance history
            </p>
          </div>
        </div>
        <Link
          to={`/attendance/${userid}`}
          className="inline-flex items-center gap-2 rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 text-[12px] font-semibold text-[#007BFF] no-underline shadow-sm transition hover:border-[#007BFF]/30 hover:bg-blue-50/50"
        >
          <FaCalendarAlt size={13} />
          Monthly view
        </Link>
      </div>

      <AttendanceKpiGrid>
        <AttendanceKpiCard
          label="This page"
          value={kpi.rows}
          icon={FaCalendarAlt}
          tone="primary"
          loading={loading}
        />
        <AttendanceKpiCard
          label="On time"
          value={kpi.onTime}
          icon={FaCheckCircle}
          tone="success"
          loading={loading}
        />
        <AttendanceKpiCard
          label="Late"
          value={kpi.late}
          icon={FaClock}
          tone="warning"
          loading={loading}
        />
        <AttendanceKpiCard
          label="Absent"
          value={kpi.absent}
          icon={FaUserTimes}
          tone="danger"
          loading={loading}
        />
      </AttendanceKpiGrid>

      <AttendanceFiltersPanel
        compact
        title="Date range"
        showClearAll={Boolean(sDate || eDate || days !== 30)}
        onClearAll={clearFilters}
      >
        <AttendanceFilterRow template="auto 1fr 1fr 9rem" minWidth="32rem">
          <AttendanceFilterQuickGroup>
            {dayPresets.slice(0, 3).map((preset) => (
              <AttendanceDateChip
                key={preset.value}
                active={days === preset.value && !sDate}
                onClick={() => applyDayPreset(preset.value)}
              >
                {preset.title.replace("Last ", "")}
              </AttendanceDateChip>
            ))}
          </AttendanceFilterQuickGroup>
          <AttendanceFilterField label="From">
            <AttendanceDateInput
              id="emp-att-from"
              narrow
              value={sDate}
              onChange={(e) => onStartDateChange(e.target.value)}
            />
          </AttendanceFilterField>
          {showEndDate ? (
            <AttendanceFilterField label="To">
              <AttendanceDateInput
                id="emp-att-to"
                narrow
                value={eDate}
                min={sDate}
                onChange={(e) => onEndDateChange(e.target.value)}
              />
            </AttendanceFilterField>
          ) : (
            <AttendanceFilterField label="Quick range">
              <AttendanceSelect
                id="emp-att-days"
                value={days ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v) applyDayPreset(Number(v));
                }}
              >
                <option value="">Custom dates…</option>
                {dayPresets.map((preset) => (
                  <option key={preset.value} value={preset.value}>
                    {preset.title}
                  </option>
                ))}
              </AttendanceSelect>
            </AttendanceFilterField>
          )}
        </AttendanceFilterRow>
      </AttendanceFiltersPanel>

      <AttendanceTableCard
        flush
        scrollable={false}
        title="Attendance log"
        extra={
          <span className="text-[11px] font-semibold text-gray-500">
            {loading ? "Loading…" : `${totalItem} records`}
          </span>
        }
        footer={
          !loading && totalItem > 0 ? (
            <Paginations
              currentPage={currentPage}
              total={totalItem}
              itemsPerPage={perPage}
              lastPage={lastPage}
              onPageChange={handlePaginationCallback}
              totalItems={totalItem}
              perItem={perItem}
            />
          ) : null
        }
      >
        {loading ? (
          <div className="space-y-2 px-3 py-6 sm:px-4">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-10 animate-pulse rounded-md bg-gray-100"
              />
            ))}
          </div>
        ) : record.length ? (
          <AttendanceTableWrap>
            <thead>
              <tr>
                <th className={ATT_TABLE_HEAD}>Employee</th>
                <th className={ATT_TABLE_HEAD_CENTER}>Check in</th>
                <th className={ATT_TABLE_HEAD_CENTER}>Check out</th>
                <th className={ATT_TABLE_HEAD_CENTER}>Hours</th>
                <th className={ATT_TABLE_HEAD_CENTER}>Status</th>
                <th className={ATT_TABLE_HEAD}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {record.map((value, index) => (
                <tr key={index} className={ATT_TABLE_ROW}>
                  <td className={ATT_TABLE_CELL}>
                    <div className="min-w-[140px]">
                      <p className="m-0 font-semibold text-[#35314d]">
                        {value?.user || "—"}
                      </p>
                      {value?.branch ? (
                        <p className="m-0 mt-0.5 text-[11px] text-gray-500">{value.branch}</p>
                      ) : null}
                    </div>
                  </td>
                  <td className={`${ATT_TABLE_CELL} text-center`}>
                    {value?.in ? (
                      <div className="inline-flex flex-col items-center gap-0.5">
                        <AttendanceTimePill
                          time={formatClock(value.in, value.timezone)}
                          variant="in"
                        />
                        <span className="text-[10px] text-gray-500">
                          {formatDateLine(value.in, value.timezone)}
                        </span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </td>
                  <td className={`${ATT_TABLE_CELL} text-center`}>
                    {value?.out ? (
                      <div className="inline-flex flex-col items-center gap-0.5">
                        <AttendanceTimePill
                          time={formatClock(value.out, value.timezone)}
                          variant="out"
                        />
                        <span className="text-[10px] text-gray-500">
                          {formatDateLine(value.out, value.timezone)}
                        </span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </td>
                  <td className={`${ATT_TABLE_CELL} text-center`}>
                    <AttendanceHoursBadge value={value?.total} />
                  </td>
                  <td className={`${ATT_TABLE_CELL} text-center`}>
                    <AttendanceStatusBadge status={value?.status} />
                  </td>
                  <td className={ATT_TABLE_CELL}>
                    {value?.notes ? (
                      <span className="inline-flex max-w-[240px] items-start gap-1.5 rounded-lg border border-sky-100 bg-sky-50/80 px-2.5 py-1.5 text-[12px] leading-snug text-sky-950">
                        <FaStickyNote className="mt-0.5 shrink-0 text-sky-600" size={12} />
                        <span className="break-words">{value.notes}</span>
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </AttendanceTableWrap>
        ) : (
          <AttendanceEmpty
            icon={FaCalendarAlt}
            title="No attendance records"
            description="Change the date range or quick filter above."
          />
        )}
      </AttendanceTableCard>
    </AttendancePageShell>
  );
};

export default AttendenceForEmployee;
